import React, { useState } from 'react';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import get from 'lodash/get';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Helpers
import { validEmail } from 'Shared/helpers';

// Hooks
import { useToggle, useQueryParam, useLocalStorage } from 'Shared/utils/hooks';
import { useApp } from 'Shared/utils/hooks/use-app';
import { useMarketingUrl } from 'Shared/utils/hooks/use-marketing-url';

// Components
import { FormProps } from 'Wpds/components/form/WpForm';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpForm, WpFormLayout } from 'Wpds/components/form';
import { WpLink } from 'Wpds/components/link';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpDivider from 'Wpds/components/divider/WpDivider';
import OAuthButton from 'Shared/components/OAuthButton/OAuthButton';
import { WpModalFooter } from 'Wpds/components/modal';

// Styles
import './SignInPage.scss';
import { WpIcon } from 'Wpds/components/icon';

const SignInPage = (): React.JSX.Element => {
  const [invalidEmailOrPasswordEntered] = useQueryParam('emailOrPasswordInvalid');
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const [showLegacyForm, setShowLegacyForm] = useState(invalidEmailOrPasswordEntered === 'true');
  const [performAccessData, setPerformAccess] = useState(null);
  const [enteredEmail, setEnteredEmail] = useLocalStorage('input-email-for-pi-detection');
  const [hasClickedSignInForTeamsEmbeddedTab, setHasClickedSignInForTeamsEmbeddedTab] = useState(false);
  const { app, isTeamsEmbeddedTab } = useApp();
  const { search } = useLocation();
  const { PIPerformMarketingUrl } = useMarketingUrl();

  const appId = app.id;
  const signInUrl = app.routes.signInUrl;
  const query = queryString.parse(search);
  const csrfValue = get(document.getElementsByName('csrf-token')[0], 'content');
  const inviterSlug = query?.inviter_slug ? query.inviter_slug.toString() : '';

  const handleSubmit = () => toggleIsSubmitting();
  const baseClassName = 'SignInPage';

  /**
   * Handles the email check for PI users.
   * @param data the data passed back from WpForm
   */
  const handleEmailCheck: FormProps['onSubmit'] = data => {
    toggleIsSubmitting();
    const email = data['input-email-for-pi-detection'];
    setEnteredEmail(email);
    const encodedEmail = encodeURIComponent(email);

    // check if email ends with @predictiveindex.com and send them to Auth0 login
    if (email.endsWith('@predictiveindex.com')) {
      window.location.href = `/token_auth/pi_auth0_login`;
      return;
    }

    fetch(`/token_auth/user_is_pi_user?email=${encodedEmail}`).then(async response => {
      const data = await response.json();
      if (data.is_pi_user) {
        setPerformAccess(data);
        if (data.has_perform_access) {
          window.location.href = `/token_auth/pi_auth0_login?email_from_login_screen=${encodedEmail}`;
        }
        toggleIsSubmitting();
      } else {
        setShowLegacyForm(true);
        toggleIsSubmitting();
      }
    });
  };

  const emailForPiDetectionForm = () => {
    return (
      <WpForm onSubmit={handleEmailCheck}>
        <WpTextField
          name="email"
          id="input-email-for-pi-detection"
          type="text"
          label="Work email"
          placeholder=""
          required
          validate={{
            isEmail: {
              callback: validEmail,
              error: 'Please enter a valid email.',
            },
          }}
          value={enteredEmail}
          showRequiredIndicator={false}
          disabled={isSubmitting}
          autoFocus
          autoComplete={false}
          qaTest="input-email-for-pi-detection"
        />
        {performAccessData && !performAccessData.has_perform_access && (
          <p className="WpInlineError">Perform credentials not found for this email</p>
        )}
        <div className="mt-3"></div>
        <WpButton btnType="submit" qaTest="btn-submit-email-for-pi-detection" loading={isSubmitting} size="lg">
          Continue
        </WpButton>
        <div className={`${baseClassName}__footer`}>
          <p className={`${baseClassName}__footer__signup-link`}>New User?</p>
          <WpLink href={PIPerformMarketingUrl}>Sign up</WpLink>
        </div>
      </WpForm>
    );
  };

  const triggerSignInFromTeamsEmbeddedTab = () => {
    window.open('/token_auth/pi_auth0_login?wp_origin=teams_embedded_tab', '_blank');
    setTimeout(() => {
      setHasClickedSignInForTeamsEmbeddedTab(true);
    }, 2000);
  };

  const signInOnMainPiAppButton = () => {
    if (hasClickedSignInForTeamsEmbeddedTab)
      return (
        <div>
          To access Perform, please sign in to the application in a web browser. Once you&apos;re done, return here and
          click &quot;I&apos;ve signed in&quot; to refresh your session.
          <div className="mt-4">
            <WpModalFooter>
              <div className="w-100 d-flex align-items-center justify-content-center">
                <WpButtonGroup>
                  <WpButton variant="secondary" onClick={triggerSignInFromTeamsEmbeddedTab}>
                    <WpIcon name="arrow-right" />
                    Retry Sign In
                  </WpButton>
                  <WpButton onClick={() => window.location.reload()}>
                    <WpIcon name="fa-check-circle" />
                    I&apos;ve signed in
                  </WpButton>
                </WpButtonGroup>
              </div>
            </WpModalFooter>
          </div>
        </div>
      );

    if (!hasClickedSignInForTeamsEmbeddedTab)
      return (
        <div>
          To access Perform, please sign in to the application in a web browser. Once you&apos;re done, return here and
          click &quot;I&apos;ve signed in&quot; to refresh your session.
          <div className="mt-4">
            <WpModalFooter>
              <div className="w-100 d-flex align-items-center justify-content-center">
                <WpButton onClick={triggerSignInFromTeamsEmbeddedTab}>Sign in</WpButton>
              </div>
            </WpModalFooter>
          </div>
        </div>
      );
  };
  const legacySignInForm = () => {
    return (
      <>
        <div className={`${baseClassName}__oauth-btns`}>
          <OAuthButton oauthProvider="google" text="Sign in with Google" />
          <OAuthButton oauthProvider="office365" text="Sign in with Office365" />
        </div>
        <WpDivider text="or" padding={16} />
        <WpForm method="post" action={signInUrl} onSubmit={handleSubmit}>
          <WpFormLayout>
            <WpTextField
              name="email"
              id="user[email]"
              type="text"
              label="Work email"
              placeholder=""
              required
              validate={{
                isEmail: {
                  callback: validEmail,
                  error: 'Please enter a valid email.',
                },
              }}
              value={enteredEmail}
              showRequiredIndicator={false}
              disabled={isSubmitting}
              autoFocus
              autoComplete={false}
              qaTest="input-signin-email"
            />
            <WpTextField
              name="password"
              id="user[password]"
              type="password"
              label="Password"
              placeholder=""
              required
              disabled={isSubmitting}
              showRequiredIndicator={false}
              autoComplete={false}
              qaTest="input-signin-password"
            />
          </WpFormLayout>
          <div style={{ visibility: 'hidden', height: '0px', position: 'absolute', top: '-1000px', left: '-1000px' }}>
            <WpTextField type="text" id="authenticity_token" label="authenticity_token" value={csrfValue} readOnly />
          </div>
          {inviterSlug && (
            <div style={{ visibility: 'hidden', height: '0px', position: 'absolute', top: '-1000px', left: '-1000px' }}>
              <WpTextField
                type="text"
                id="inviter_slug"
                name="inviter_slug"
                label="inviter_slug"
                value={inviterSlug}
                readOnly
              />
            </div>
          )}
          <WpButton size="lg" btnType="submit" block loading={isSubmitting} qaTest="btn-sign-in-with-email-submit">
            Sign In
          </WpButton>
          <div className={`${baseClassName}__footer`}>
            <p className={`${baseClassName}__footer__forgot-password-link`}>
              <a href={`${PageUrls.RESET_PASSWORD_NEW}?app_id=${appId}`} rel="noopener noreferrer">
                Forgot password
              </a>
            </p>
          </div>
        </WpForm>
      </>
    );
  };

  if (isTeamsEmbeddedTab) {
    return <>{signInOnMainPiAppButton()}</>;
  }

  return (
    <div className={baseClassName}>
      <h3 className={`${baseClassName}__title`}>Sign In</h3>
      {!showLegacyForm && emailForPiDetectionForm()}
      {showLegacyForm && legacySignInForm()}
    </div>
  );
};

export default SignInPage;
